import { useCallback, useEffect, useRef, useState } from "react";
import { API, http } from "@/lib/api";

const newSession = () => {
  const id = `s-${crypto.randomUUID()}`;
  sessionStorage.setItem("hiq_session", id);
  return id;
};
const getSession = () => sessionStorage.getItem("hiq_session") || newSession();

const MSGS_KEY = "hiq_msgs";
const loadMsgs = () => {
  try {
    return JSON.parse(sessionStorage.getItem(MSGS_KEY) || "[]").filter((m) => !m.pending);
  } catch {
    return [];
  }
};

export function useChat({ lang, page }) {
  const [messages, setMessages] = useState(loadMsgs);
  useEffect(() => {
    if (!messages.some((m) => m.pending)) sessionStorage.setItem(MSGS_KEY, JSON.stringify(messages.slice(-30)));
  }, [messages]);
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const abortRef = useRef(null);
  const session = useRef(getSession());

  const patch = (id, fn) => setMessages((ms) => ms.map((m) => (m.id === id ? { ...m, ...fn(m) } : m)));

  const send = useCallback(async (text, opts = {}) => {
    const q = text.trim();
    if (!q || busyRef.current) return;
    busyRef.current = true;
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    const localId = `a-${Date.now()}`;
    setMessages((ms) => [...ms, { id: `u-${Date.now()}`, role: "user", text: q }, { id: localId, role: "assistant", text: "", components: [], sources: [], pending: true }]);
    setBusy(true);
    try {
      const res = await fetch(`${API}/chat/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: session.current, message: q, lang, page: opts.voice ? { ...(page || {}), voice: true } : page }),
        signal: ctrl.signal,
      });
      if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`);
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const p of parts) {
          const line = p.trim();
          if (!line.startsWith("data:")) continue;
          const ev = JSON.parse(line.slice(5));
          if (ev.type === "start") patch(localId, () => ({ messageId: ev.message_id }));
          if (ev.type === "text") patch(localId, (m) => ({ text: m.text + ev.content, tool: null }));
          if (ev.type === "retract") patch(localId, (m) => ({ text: m.text.slice(0, Math.max(0, m.text.length - ev.chars)) }));
          if (ev.type === "tool") patch(localId, () => ({ tool: ev.name }));
          if (ev.type === "component") patch(localId, (m) => ({ components: [...m.components, ev.data] }));
          if (ev.type === "sources") patch(localId, () => ({ sources: ev.items }));
          if (ev.type === "done") patch(localId, () => ({ pending: false }));
        }
      }
    } catch (e) {
      if (ctrl.signal.aborted) {
        patch(localId, (m) => ({ text: m.text || "…", interrupted: true }));
        return;
      }
      patch(localId, (m) => ({ text: m.text || (lang === "fr" ? "Désolé, une erreur est survenue. Veuillez réessayer." : "Sorry, something went wrong. Please try again."), error: true }));
    } finally {
      patch(localId, () => ({ pending: false }));
      if (abortRef.current === ctrl) {
        busyRef.current = false;
        abortRef.current = null;
        setBusy(false);
      }
    }
  }, [lang, page]);

  // Stops the answer that is streaming (used when the user talks over Ceres).
  const cancel = useCallback(() => {
    if (!abortRef.current) return;
    abortRef.current.abort();
    abortRef.current = null;
    busyRef.current = false;
    setBusy(false);
  }, []);

  const feedback = useCallback(async (msg, value) => {
    if (!msg.messageId) return;
    patch(msg.id, () => ({ feedback: value }));
    await http.post("/chat/feedback", { message_id: msg.messageId, value }).catch(() => {});
  }, []);

  const [sessionId, setSessionId] = useState(session.current);
  const reset = useCallback(() => {
    session.current = newSession();
    setSessionId(session.current);
    setMessages([]);
  }, []);

  const push = useCallback((...ms) => setMessages((cur) => [...cur, ...ms]), []);

  return { messages, busy, send, cancel, feedback, reset, push, sessionId };
}
